import React, { Fragment, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';

import Button from '../../../../../../components/global/Button';
import { getErrorString } from '../../../../../../util/normalize-errors';

const propTypes = {
  photoId: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  album: PropTypes.shape({
    id: PropTypes.string,
  }),
  albumFolder: PropTypes.shape({
    id: PropTypes.string,
  }),
  updateAlbum: PropTypes.func,
  updateAlbumFolder: PropTypes.func,
  requestConfirm: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.string,
    PropTypes.func,
  ]),
};

const CoverPhotoButton = ({
  photoId,
  loading,
  album,
  albumFolder,
  updateAlbum,
  updateAlbumFolder,
  requestConfirm,
  children,

  staticContext,
  ...btnProps
}) => {
  const btnRef = useRef(null);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (success || error) {
      ReactTooltip.show(btnRef.current);
    }
  }, [success, error]);

  useEffect(() => {
    setSuccess(false);
    setError('');
    ReactTooltip.hide(btnRef.current);
  }, [photoId]);

  const updateCoverPhoto = () => {
    const input = {
      id: album ? album.id : albumFolder.id,
      custom_cover_photos: [{ display_order: 0, photo_id: photoId }],
    };

    requestConfirm({
      message: (
        <Fragment>
          <p>
            You are about to use this photo as a cover photo in this{' '}
            {album ? 'album' : 'gallery'}. This will remove any cover photos you
            previously set up. Please confirm you want this to be your cover
            photo.
          </p>
          <p className="text-muted text-xs">
            You may need to refresh to see the changes reflected
          </p>
        </Fragment>
      ),
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      onConfirm: async () => {
        setSuccess(false);
        setError('');
        setUpdating(true);

        try {
          if (album) {
            await updateAlbum(input);
          } else {
            await updateAlbumFolder(input);
          }

          setSuccess(true);
        } catch (updateError) {
          setError(getErrorString(updateError));
        } finally {
          setUpdating(false);
        }
      },
      onCancel: () => {
        // Do nothing
      },
    });
  };

  if (!loading && !album && !albumFolder) {
    return null;
  }

  const tooltipLabel = success ? 'Cover photo updated!' : error;

  if (typeof children === 'function') {
    return children({ updateCoverPhoto, loading: loading || updating });
  }

  return (
    <Button
      onClick={updateCoverPhoto}
      {...btnProps}
      disabled={btnProps?.disabled || loading || updating}
      inputRef={btnRef}
      data-for="tooltip-cover-button"
      data-tip={tooltipLabel}
      data-effect="solid"
    >
      {updating ? 'Updating...' : children || 'Use as cover photo'}
      <ReactTooltip
        id="tooltip-cover-button"
        className="tooltip"
        delayShow={100}
      />
    </Button>
  );
};

CoverPhotoButton.propTypes = propTypes;

export default CoverPhotoButton;
